import { BreakpointObserver } from '@angular/cdk/layout';
import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs';

import { WhatViewport } from '@digital-platform/rs-cms/domain';

const px = (size: string, offset: number = 0) =>
    `${BREAKPOINTS[size] + offset}px`,
  minW = (size: string) => `(min-width: ${px(size)})`,
  maxW = (size: string) => `(max-width: ${px(size, -1)})`,
  minMaxW = (minSize: string, maxSize: string) =>
    `${minW(minSize)} and ${maxW(maxSize)}`;

export const BREAKPOINTS: { [name: string]: number } = {
    xs: 0, //..... mobile
    sm: 576, //... mobile
    md: 768, //... midsize
    lg: 992, //... midsize
    xl: 1200, //.. desktop
    xxl: 1440, //. desktop
  },
  // Logical breakpoints
  MOBILE = maxW('md'),
  MIDSIZE = minMaxW('md', 'xl'),
  DESKTOP = minW('xl'),
  // Named breakpoints
  XS = maxW('sm'),
  SM = minMaxW('sm', 'md'),
  SM_AND_UP = minW('sm'),
  SM_AND_DOWN = maxW('md'),
  MD = minMaxW('md', 'lg'),
  MD_AND_UP = minW('md'),
  MD_AND_DOWN = maxW('lg'),
  LG = minMaxW('lg', 'xl'),
  LG_AND_UP = minW('lg'),
  LG_AND_DOWN = maxW('xl'),
  XL = minMaxW('xl', 'xxl'),
  XL_AND_UP = minW('xl'),
  XL_AND_DOWN = maxW('xxl'),
  XXL = minW('xxl'),
  // other breakpoints
  DESKTOP_AND_UP = DESKTOP,
  MIDSIZE_AND_UP = minW('md'),
  MIDSIZE_AND_DOWN = maxW('xl');

@Injectable({
  providedIn: 'root',
})
export class ViewportService {
  private breakpointObserver = inject(BreakpointObserver);

  public isMobile = false;
  public isMidsize = false;
  public isDesktop = false;

  // prettier-ignore
  public whatViewport: WhatViewport = {
    mobile: false, midsize: false, desktop: false,
    xs: false,
    sm: false, smUp: false, smDown: false,
    md: false, mdUp: false, mdDown: false,
    lg: false, lgUp: false, lgDown: false,
    xl: false, xlUp: false, xlDown: false,
    xxl: false
  };

  constructor() {
    this.whatViewport$.subscribe((result) => {
      this.isMobile = result.mobile;
      this.isMidsize = result.midsize;
      this.isDesktop = result.desktop;
      // this.expose();
    });
  }

  public isMobile$ = this.breakpointObserver
    .observe([MOBILE])
    .pipe(map((result) => result?.matches || false));

  public isTablet$ = this.breakpointObserver.observe([MIDSIZE]).pipe(
    map((result) => {
      console.log(result.breakpoints);
      return result?.matches || false;
    })
  );

  public isDesktopAndUp$ = this.breakpointObserver
    .observe([DESKTOP_AND_UP])
    .pipe(map((result) => result?.matches || false));

  public isMidsizeAndDown$ = this.breakpointObserver
    .observe([MIDSIZE_AND_DOWN])
    .pipe(map((result) => result?.matches || false));

  public isMidsizeAndUp$ = this.breakpointObserver
    .observe([MIDSIZE_AND_UP])
    .pipe(map((result) => result?.matches || false));

  public isDesktopAndAbove$ = this.breakpointObserver
    .observe([DESKTOP])
    .pipe(map((result) => result?.matches || false));

  // Custom breakpoint sniffer
  // prettier-ignore
  public whatViewport$ = this.breakpointObserver
    .observe([
      MOBILE, MIDSIZE, DESKTOP,
      XS,
      SM, SM_AND_UP, SM_AND_DOWN,
      MD, MD_AND_UP, MD_AND_DOWN,
      LG, LG_AND_UP, LG_AND_DOWN,
      XL, XL_AND_UP, XL_AND_DOWN,
      XXL,
    ])
    .pipe(
      map(
        (result): WhatViewport => (this.whatViewport = {
          // named breakpoints
          xs: result.breakpoints[XS],
          sm: result.breakpoints[SM], smUp: result.breakpoints[SM_AND_UP], smDown: result.breakpoints[SM_AND_DOWN],
          md: result.breakpoints[MD], mdUp: result.breakpoints[MD_AND_UP], mdDown: result.breakpoints[MD_AND_DOWN],
          lg: result.breakpoints[LG], lgUp: result.breakpoints[LG_AND_UP], lgDown: result.breakpoints[LG_AND_DOWN],
          xl: result.breakpoints[XL], xlUp: result.breakpoints[XL_AND_UP], xlDown: result.breakpoints[XL_AND_DOWN],
          xxl: result.breakpoints[XXL],
          // logical breakpoints
          mobile: result.breakpoints[MOBILE],
          midsize: result.breakpoints[MIDSIZE],
          desktop: result.breakpoints[DESKTOP],
        })
      )
    );
  // Commented out to avoid breaking SSR.
  // private expose() {
  //   const breakpoint = this.isDesktop
  //     ? 'desktop'
  //     : this.isMidsize
  //     ? 'midsize'
  //     : 'mobile';
  //   let viewport = 'n/a';
  //   for (const bp of Object.keys(BREAKPOINTS)) {
  //     if (this.whatViewport[bp as keyof WhatViewport]) {
  //       viewport = bp;
  //       break;
  //     }
  //   }
  //   document.body.setAttribute('data-viewport', viewport);
  //   document.body.setAttribute('data-breakpoint', breakpoint);
  //   document.body.setAttribute('data-desktop', String(this.isDesktop));
  //   document.body.setAttribute('data-midsize', String(this.isMidsize));
  //   document.body.setAttribute('data-mobile', String(this.isMobile));
  // }
}
